import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getProject } from '../lib/api'
import { useLang } from '../lib/useLang'

// Custom icons for each section
function WorkflowIcon({ className }) {
  return (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className={className}>
      <path d='M22 12h-4l-3 9L9 3l-3 9H2' />
    </svg>
  )
}

function ResultsIcon({ className }) {
  return (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className={className}>
      <path d='M3 3v18h18' />
      <path d='m19 9-5 5-4-4-3 3' />
    </svg>
  )
}

function InferenceIcon({ className }) {
  return (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className={className}>
      <path d='M13 2L3 14h9l-1 8 10-12h-9l1-8z' />
    </svg>
  )
}

function MonitoringIcon({ className }) {
  return (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.5' className={className}>
      <rect x='2' y='3' width='20' height='14' rx='2' ry='2' />
      <line x1='8' y1='21' x2='16' y2='21' />
      <line x1='12' y1='17' x2='12' y2='21' />
      <path d='M6 10l3-3 3 3 6-6' />
    </svg>
  )
}

function LockIcon({ className }) {
  return (
    <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' className={className}>
      <rect x='3' y='11' width='18' height='11' rx='2' ry='2' />
      <path d='M7 11V7a5 5 0 0 1 10 0v4' />
    </svg>
  )
}

export default function ProjectSidebar({
  currentId,
  collapsed,
  onToggle,
  hasCompleted,
  onOpenFlowInfo,
  onOpenArtifacts,
  onOpenInference,
  onOpenMonitoring,
  activeSection = 'flow',
  onHelpClick,
}) {
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { t } = useLang()

  useEffect(() => {
    const load = async () => {
      if (!currentId) {
        setProject(null)
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        const p = await getProject(currentId)
        setProject(p)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [currentId])

  const sections = [
    { id: 'flow', label: t('sidebar.workflow'), icon: WorkflowIcon, onClick: onOpenFlowInfo, requiresModel: false },
    { id: 'artifacts', label: t('sidebar.results'), icon: ResultsIcon, onClick: onOpenArtifacts, requiresModel: true },
    { id: 'inference', label: t('sidebar.inference'), icon: InferenceIcon, onClick: onOpenInference, requiresModel: true },
    { id: 'monitoring', label: t('sidebar.monitoring'), icon: MonitoringIcon, onClick: onOpenMonitoring, requiresModel: true },
  ]

  return (
    <div
      className='flex h-full flex-col'
      style={{
        background: 'var(--cream)',
        borderRight: '1px solid var(--hairline-strong)',
      }}
    >
      {collapsed ? (
        <div className='flex h-full flex-col items-center gap-2 py-5'>
          <button
            onClick={onToggle}
            className='mb-4 flex h-9 w-9 items-center justify-center rounded-xl text-ink transition-all hover:bg-ink/[0.05] hover:text-ink'
          >
            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <path d='m9 18 6-6-6-6' />
            </svg>
          </button>
          {sections.map(section => {
            const Icon = section.icon
            const isActive = activeSection === section.id
            const isLocked = section.requiresModel && !hasCompleted
            return (
              <button
                key={section.id}
                onClick={isLocked ? undefined : section.onClick}
                disabled={isLocked}
                className={`relative flex h-11 w-11 items-center justify-center rounded-xl transition-all ${
                  isLocked
                    ? 'cursor-not-allowed text-ink opacity-40'
                    : isActive
                    ? 'bg-accent/15 text-accent shadow-lg shadow-[0_4px_14px_-6px_rgba(255,90,31,0.35)]'
                    : 'text-ink hover:bg-ink/[0.05] hover:text-ink'
                }`}
                title={isLocked ? `${section.label} (${t('sidebar.completeFirst')})` : section.label}
              >
                <Icon className={isActive && !isLocked ? 'text-accent' : ''} />
                {isLocked && (
                  <span className='absolute bottom-0 right-0 flex h-4 w-4 items-center justify-center rounded-full bg-ink/80'>
                    <LockIcon className='text-ink' />
                  </span>
                )}
              </button>
            )
          })}
          {onHelpClick && (
            <button
              type='button'
              onClick={onHelpClick}
              className='pv-help-btn pv-help-btn--icon mt-auto'
              title={t('sidebar.howToUse') || 'Как пользоваться'}
            >
              <span className='pv-help-btn__glow' aria-hidden='true' />
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <circle cx='12' cy='12' r='10' />
                <path d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' />
                <line x1='12' y1='17' x2='12.01' y2='17' />
              </svg>
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Project Header */}
          <div className='border-b border-[var(--hairline)] px-5 py-4'>
            <div className='mb-1 flex items-center justify-between'>
              <button
                onClick={() => navigate('/app')}
                className='flex items-center gap-2 text-xs text-ink transition-colors hover:text-ink'
              >
                <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <path d='m15 18-6-6 6-6' />
                </svg>
                {t('sidebar.allProjects')}
              </button>
              <button
                onClick={onToggle}
                className='flex h-6 w-6 items-center justify-center rounded text-ink transition-colors hover:bg-ink/[0.05] hover:text-ink'
              >
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                  <path d='m15 18-6-6 6-6' />
                </svg>
              </button>
            </div>
            {loading ? (
              <div className='h-6 w-32 animate-pulse rounded bg-ink/[0.05]' />
            ) : (
              <h2 className='truncate text-base font-medium text-ink'>{project?.name || t('sidebar.project')}</h2>
            )}
          </div>

          {/* Navigation Sections */}
          <nav className='flex-1 px-3 py-4'>
            <div className='space-y-1'>
              {sections.map(section => {
                const Icon = section.icon
                const isActive = activeSection === section.id
                const isLocked = section.requiresModel && !hasCompleted
                return (
                  <button
                    key={section.id}
                    onClick={isLocked ? undefined : section.onClick}
                    disabled={isLocked}
                    className={`group flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all ${
                      isLocked
                        ? 'cursor-not-allowed opacity-40'
                        : isActive
                        ? 'bg-accent/10 text-ink'
                        : 'text-ink hover:bg-ink/[0.05] hover:text-ink'
                    }`}
                  >
                    <span
                      className={`relative flex h-9 w-9 items-center justify-center rounded-lg transition-all ${
                        isLocked
                          ? 'bg-ink/[0.05] text-ink'
                          : isActive
                          ? 'bg-accent/15 text-accent shadow-lg shadow-[0_4px_14px_-6px_rgba(255,90,31,0.35)]'
                          : 'bg-ink/[0.05] text-ink group-hover:text-ink'
                      }`}
                    >
                      <Icon />
                      {isLocked && (
                        <span className='absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-ink/80 ring-2 ring-cream'>
                          <LockIcon className='text-ink' />
                        </span>
                      )}
                    </span>
                    <span className={`text-sm font-medium ${isLocked ? 'text-ink' : ''}`}>
                      {section.label}
                    </span>
                    {!isLocked && isActive && (
                      <span className='ml-auto h-1.5 w-1.5 rounded-full bg-accent/15 shadow-lg shadow-[0_4px_14px_-6px_rgba(255,90,31,0.35)]' />
                    )}
                    {isLocked && (
                      <span className='ml-auto text-xs text-ink'>{t('sidebar.locked')}</span>
                    )}
                  </button>
                )
              })}
            </div>
          </nav>

          {onHelpClick && (
            <div className='px-3 pb-4 pt-1'>
              <button
                type='button'
                onClick={onHelpClick}
                className='pv-help-btn'
              >
                <span className='pv-help-btn__glow' aria-hidden='true' />
                <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                  <circle cx='12' cy='12' r='10' />
                  <path d='M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' />
                  <line x1='12' y1='17' x2='12.01' y2='17' />
                </svg>
                {t('sidebar.howToUse') || 'Как пользоваться'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
